<script lang="ts">
  import {replaceValues, t} from '../i18n'
  import type {Collection, Poem} from '../types'
  import api from '../api/api'
  import {user} from '../stores/user'
  import SearchInput from '../shared/SearchInput.svelte'
  import Placeholder from '../shared/Placeholder.svelte'
  import LoadMore from '../shared/LoadMore.svelte'
  import {modal} from '../stores/modal'
  import CollectionFollowers from './CollectionFollowers.svelte'
  import CollectionForm from './CollectionForm.svelte'
  import SmallPoem from '../poems/SmallPoem.svelte'
  import NotFoundPage from '../../src/NotFoundPage.svelte'
  import SpinnerSection from '../../src/shared/SpinnerSection.svelte'

  export let id: string

  let collection: Collection | undefined
  let poems: Poem[] | undefined
  let search = ''
  let loaded = false

  $: if (id) fetchCollection(id)

  async function fetchCollection(id: string) {
    loaded = false
    collection = await api.get<Collection>(`/collections/${id}`).catch(() => undefined).finally(() => loaded = true)
  }

  async function follow(e: Event) {
    e.preventDefault()
    if (!collection) return
    await api.put(`/collections/${collection.id}/follow`)
    collection.is_followed = true
    collection.followers_count += 1
  }

  async function unfollow(e: Event) {
    e.preventDefault()
    if (!collection) return
    await api.put(`/collections/${collection.id}/unfollow`)
    collection.is_followed = false
    collection.followers_count -= 1
  }

  async function uploadPicture(event: Event) {
    const input = event.target as HTMLInputElement
    const file = input.files ? input.files[0] : null
    if (!file || !collection) return

    const formData = new FormData()
    formData.append('collection[image]', file)

    collection = await api.put<Collection>(`/collections/${collection.id}`, formData)
  }

  async function deleteImage() {
    if (!collection) return
    collection = await api.put<Collection>(`/collections/${collection.id}`, {
      collection: {
        ...collection,
        remove_image: true
      }
    })
  }

  function openFollowers(e: Event) {
    e.preventDefault()
    modal.show(CollectionFollowers, {props: {collection}})
  }

  function onUpdated(updated: Collection) {
    collection = updated
  }

  function openEditForm(e: Event) {
    e.preventDefault()
    modal.show(CollectionForm, {props: {collection, onSaved: onUpdated}})
  }
</script>

{#if collection}
  <div class="row">
    <div class="collection__header">
      {#if $user?.id === collection.user_id}
        <div class="collection__actions">
          <span class="wh_round icon-p-edit" onclick={openFollowers}/>
          <label for="image">
            <span class="wh_round icon-p-share"></span>
            <input id="image" type="file" onchange={uploadPicture} style="display: none;"/>
          </label>
          {#if collection.image_present}
            <span class="wh_round icon-p-trash" onclick={deleteImage}/>
          {/if}
        </div>
      {/if}

      <div class="collection__header__content">
        <h1 class="collection__title">{collection.name}</h1>
        <div class="collection__info">
          <span class="collection__poem__count">
            <i class="icon-p-book"></i>
            {replaceValues(t.collection.poems_count, {n: collection.poems_count})}
          </span>
          <span class="collection__status">
            <i class="icon-p-eye"></i>
            {#if collection.public}
              {t.collection.public}
            {:else}
              {t.collection.private}
            {/if}
          </span>
          <a class="collection__creator" href={`/authors/${collection.user_handle}`}>
            <i class="icon-p-user"></i>
            {replaceValues(t.collection.by, {name: collection.author_full_name})}
          </a>
        </div>

        {#if $user && $user.id !== collection.user_id}
          <div class="collection__follow">
            {#if collection.is_followed}
              <button class="ui button white" onclick={unfollow}>{t.collection.board_followed}</button>
            {:else}
              <button class="ui button white" onclick={follow}>{t.collection.follow_board}</button>
            {/if}
          </div>
        {:else if $user?.id === collection.user_id}
          <button class="ui button white" onclick={openEditForm}>
            {replaceValues(t.collection.followers_count, {n: collection.followers_count})}
          </button>
        {:else}
          <button class="ui button white">
            {replaceValues(t.collection.followers_count, {n: collection.followers_count})}
          </button>
        {/if}
      </div>

      <div class="collection__cover" style="background-image: url('{collection.image_url}');"/>
    </div>

    <div class="grid-section">
      <div class="heading inner-heading">
        <SearchInput bind:search placeholder={t.collection.find}/>
      </div>

      {#if poems && poems.length}
        <div class="grid three">
          {#each poems as poem}
            <div class="grid__item">
              <SmallPoem {poem}/>
            </div>
          {/each}
        </div>
      {:else}
        <Placeholder placeholder={t.collection.no_poems} loading={!poems}/>
      {/if}

      <LoadMore bind:items={poems} url={`/collections/${collection.id}/poems`} {search}/>
    </div>
  </div>
{:else if loaded}
  <NotFoundPage/>
{:else}
  <SpinnerSection/>
{/if}
